import React, { useState } from "react"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDAvatar from "components/MDAvatar"
import MDBadge from "components/MDBadge"
import PropTypes from "prop-types"
import { PhotoProvider, PhotoView } from "react-photo-view"
import "react-photo-view/dist/react-photo-view.css"
import { Tooltip } from "@mui/material"
import { useMaterialUIController } from "context"
import { useTheme } from "@emotion/react"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined"

export const mapDataToRows = (data, config, page) => {
  return data.map((row, index) => {
    let dateFormat = row.dateTime.substring(0, 19).replace("T", " ") //formatFecha(row.dateTime);

    let url = `${config.api.baseURL}${row.imagePath}`
    const modifiedUrl = url.replace("/api.", "")
    let parsedMatches = []
    let corsightData = ""
    //Matches
    try {
      if (typeof row.matches === "string") {
        parsedMatches = JSON.parse(row.matches)
      } else if (Array.isArray(row.matches)) {
        parsedMatches = row.matches
      }
    } catch (e) {
      console.error("No se pudo parsear 'matches':", e)
    }
    try {
      const raw = typeof row.corsight === "string" ? JSON.parse(row.corsight) : row.corsight
      corsightData = typeof raw === "string" ? JSON.parse(raw) : raw
    } catch (e) {
      console.error("Error parseando corsight:", e)
    }
    const person = (
      <Author
        image={modifiedUrl}
        name={row.nombre}
        number={row.documentNumber}
        type={row.documentType}
        matches={parsedMatches}
        corsight={corsightData}
      />
    )
    const device = (
      <Job
        title={(row.snapshotDeviceType ?? row.device?.type) === "PORTAL_ACCESO" ? "PORTAL" : ""}
        description={row.snapshotDeviceDesc || row.device?.description || ""}
        ip={row.ipDevice}
      />
    )

    const serialNo = <Job title="" description={row.serialNo} ip="" />
    const status = (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={row.nombre == null ? "No Reconocido" : "Reconocido"}
          color={row.nombre == null ? "error" : "success"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    )
    const sucursal = (
      <Job
        title={row.snapshotEmpresaDesc || row.device?.sucursales?.empresa?.descripcion}
        description={row.snapshotSucursalDesc || row.device?.sucursales?.descripcion}
        ip={""}
      />
    )
    const huella = (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={row.fingerprint === null ? "No" : "Si"}
          color={row.fingerprint === null ? "error" : "success"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    )
    const esCliente = (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={row.esCliente === null ? "No" : row.esCliente ? "Si" : "No"}
          color={row.esCliente === null ? "error" : row.esCliente ? "success" : "error"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    )
    const entradaSalida = (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={
            row.inOut === null || row.inOut === ""
              ? "N/A"
              : row.inOut === "in"
                ? "Entrada"
                : "Salida"
          }
          color={
            row.inOut === null || row.inOut === ""
              ? "dark"
              : row.inOut === "in"
                ? "success"
                : "error"
          }
          variant="gradient"
          size="sm"
        />
      </MDBox>
    )
    const dateTime = (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {dateFormat}
      </MDTypography>
    )
    const item = page * 10 + (index + 1)

    return {
      item: item,
      person: person,
      device: device,
      status: status,
      huella: huella,
      esCliente: esCliente,
      entradaSalida: entradaSalida,
      serialNo: serialNo,
      dateTime: dateTime,
      sucursal: sucursal,
    }
  })
}

const Author = ({ image, name, number, type, matches, corsight = "Sin datos" }) => {
  const [open, setOpen] = useState(false)
  const [controller] = useMaterialUIController()
  const { darkMode } = controller
  const handleToggle = () => setOpen((prev) => !prev)
  const handleClose = () => setOpen(false)
  const theme = useTheme()

  const renderCorsightTooltip = (raw) => {
    try {
      const data = typeof raw === "string" ? JSON.parse(raw) : raw
      const list = data?.data?.matches ?? data?.matches ?? []
      const msg = data?.metadata?.msg

      if (!Array.isArray(list) || list.length === 0) {
        return (
          <MDBox>
            <MDTypography variant="caption" display="block">
              {msg === "success" && (
                <CheckCircleIcon
                  sx={{
                    fontSize: 14,
                    color: "success.main",
                    verticalAlign: "middle",
                    mr: 0.5,
                  }}
                />
              )}
              Sin coincidencias
            </MDTypography>
          </MDBox>
        )
      }
      return (
        <MDBox>
          {list.map((m, i) => {
            const conf =
              m.poi_confidence ??
              m.confidence ??
              (Array.isArray(m.faces) && m.faces[0]?.confidence) ??
              null
            const watchlistName =
              Array.isArray(m.watchlists) && m.watchlists.length > 0
                ? m.watchlists.map((w) => w.display_name).join(", ")
                : "—"
            return (
              <>
                <MDTypography
                  key={i}
                  variant="caption"
                  display="block"
                  sx={{ textAlign: "left", paddingLeft: "5px", paddingBottom: "12px" }}
                >
                  {msg === "success" && (
                    <CheckCircleIcon
                      sx={{
                        fontSize: 14,
                        color: "success.main",
                        verticalAlign: "middle",
                        mr: 0.5,
                      }}
                    />
                  )}
                  <b>DOC:</b> {m.poi_id} {" - "} <br />
                  <b>Nombre:</b> {m.display_name} <br />
                  <b>Confidence:</b> {conf != null ? `${conf.toFixed(2)}%` : "—"} <br />
                  <b>Watchlist:</b> {watchlistName}
                  <br />
                </MDTypography>
              </>
            )
          })}
        </MDBox>
      )
    } catch {
      return "Sin datos"
    }
  }

  return (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <PhotoProvider>
        <PhotoView src={image}>
          <MDAvatar src={image} name={name} size="xxl" style={{ cursor: "pointer" }} />
        </PhotoView>
      </PhotoProvider>
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="light">
          <Tooltip
            title={renderCorsightTooltip(corsight)}
            open={open}
            onClose={handleClose}
            disableFocusListener
            disableHoverListener
            disableTouchListener
          >
            <InfoOutlineIcon
              fontSize="small"
              style={{
                cursor: "pointer",
                color: darkMode ? theme.palette.warning.light : theme.palette.warning.main,
              }}
              onClick={handleToggle}
            />
          </Tooltip>{" "}
          {`${name === null ? "NO IDENTIFICADO" : name}`}
          <br />
          <small>
            {type === null ? "" : type + ": "}
            {number === null
              ? ""
              : isNaN(Number(number))
              ? number
              : new Intl.NumberFormat("es-PY", { minimumFractionDigits: 0 }).format(Number(number))}
          </small>
        </MDTypography>
      </MDBox>
    </MDBox>
  )
}

Author.propTypes = {
  image: PropTypes.string.isRequired,
  name: PropTypes.string,
  number: PropTypes.string,
  type: PropTypes.string,
  matches: PropTypes.string,
  corsight: PropTypes.string,
}

const Job = ({ title, description, ip }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography
      display="block"
      variant="caption"
      color="text"
      fontWeight="medium"
      style={{ textAlign: "center", lineHeight: 2 }}
    >
      {title || ""}
    </MDTypography>
    <MDTypography display="block" variant="caption" style={{ textAlign: "center", lineHeight: 2 }}>
      {description || ""}
    </MDTypography>
    <MDTypography variant="caption" style={{ textAlign: "center" }}>
      <center>{ip || ""}</center>
    </MDTypography>
  </MDBox>
)

Job.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  ip: PropTypes.string,
}
